import { Table } from '@mui/joy';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { RootState } from '../../../../../../utils/store';

import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import { useTenantSlug } from '~/features/tenant/helpers';
import { ReportService } from '~/services/api/reports/endpoints';

interface InformedIndividualSectionProps {
  setIsRemove?: (isRemove: boolean) => void;
}

const columns = [
  'Name',
  'Associated Department',
  'Notification Reason',
  'Date Informed',
];

const InformedIndividualSection = ({
  setIsRemove: setIsRemoveCallback,
}: InformedIndividualSectionProps) => {
  const slug = useTenantSlug();
  const reportId = z.coerce.string().catch('').parse(useParams().reportId);

  const departmentsQuery = ReportService.endpoints.getDepartmentList.useQuery(
    { slug },
    { skip: !slug },
  );
  const departments = departmentsQuery.data ?? [];

  const finalReportItem = useSelector(
    (state: RootState) => state.finalReport.item,
  );

  const query = ReportService.endpoints.reportInformedIndividuals.useQuery({
    reportId,
  });
  // get all report informed individuals based on report id (reference ids)
  const informedIndividualsItems =
    query.data?.results.filter((i) => i.report === reportId) ?? [];

  const [isRemove, setIsRemove] = useState(false);
  const remove = () => {
    if (setIsRemoveCallback) setIsRemoveCallback(false);
    setIsRemove(true);
  };

  const getDepartmentName = (id: number): string => {
    let departmentName = '';
    const findDepartment = _.find(departments, (e) => e.id == id);
    if (typeof findDepartment != 'undefined')
      departmentName = findDepartment.name;

    return departmentName;
  };

  return (
    <div className='flex flex-col mb-2'>
      {isRemove && (
        <div className='flex flex-row justify-center items-center gap-2'>
          <div className='flex align-middle'>
            <Text className='text-base font-normal text-red-500 italic'>
              <FormattedMessage defaultMessage='Informed Individuals has been removed.' />
            </Text>
          </div>
          <Button
            variant='plain'
            color='neutral'
            onClick={() => {
              setIsRemove(false);
              if (setIsRemoveCallback) setIsRemoveCallback(true);
            }}
          >
            <FormattedMessage defaultMessage='Undo changes' />
          </Button>
        </div>
      )}
      {!isRemove && (
        <>
          <div className='flex flex-row justify-between items-center'>
            <Text level='body-md' className='text-base text-gray-500'>
              <FormattedMessage defaultMessage='Informed Individuals' />
            </Text>
            {finalReportItem.status === 0 && (
              <Button color='danger' variant='plain' onClick={remove}>
                <FormattedMessage defaultMessage='Remove' />
              </Button>
            )}
          </div>
          <div className='flex flex-row'>
            <Table
              sx={{
                '--TableCell-headBackground': '#F6F6F6',
                '--TableCell-cornerRadius': '10px 10px 0px 0px',
              }}
              className='mt-3 border-spacing-0 border-collapse border border-gray-300 overflow-hidden'
            >
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={`tc-${column}`}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(informedIndividualsItems.length === 0 ||
                  (!finalReportItem.has_informed_individuals &&
                    finalReportItem.status === 1)) && (
                  <tr>
                    <td className='text-center' colSpan={4}>
                      <FormattedMessage defaultMessage='No informed individuals found.' />
                    </td>
                  </tr>
                )}
                {((informedIndividualsItems.length > 0 &&
                  finalReportItem.has_informed_individuals &&
                  finalReportItem.status === 1) ||
                  (informedIndividualsItems.length > 0 &&
                    finalReportItem.status === 0)) &&
                  informedIndividualsItems.map((item) => (
                    <tr key={`tr-${item.id}`}>
                      <td>
                        <div>{item.name}</div>
                      </td>
                      <td>{getDepartmentName(item.associated_department)}</td>
                      <td>
                        <div>{item.notification_reason}</div>
                      </td>
                      <td>{dayjs(item.date_informed_at).format('DD-MMM-YYYY')}</td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default InformedIndividualSection;
